// utils/assignedListSorting.js

const LEGACY_PRIORITY_VALUES = {
  urgent: 3,
  high: 3,
  normal: 2,
  regular: 2,
  low: 1,
};

const normalizePriorityValue = (priority, fallback = 2) => {
  if (typeof priority === "number" && Number.isFinite(priority)) {
    return Math.max(1, Math.round(priority));
  }

  if (
    typeof priority?.value === "number" &&
    Number.isFinite(priority.value)
  ) {
    return Math.max(1, Math.round(priority.value));
  }

  if (Array.isArray(priority)) {
    const active = priority.find((item) => item?.active === true);

    return (
      LEGACY_PRIORITY_VALUES[
        String(active?.name || "").toLowerCase()
      ] ?? fallback
    );
  }

  return fallback;
};

const toMillis = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  if (typeof value?.toDate === "function") {
    return value.toDate().getTime();
  }

  if (typeof value?.seconds === "number") {
    return (
      value.seconds * 1000 +
      Math.floor((value.nanoseconds || 0) / 1e6)
    );
  }

  return null;
};

const compareText = (a, b) =>
  String(a || "").localeCompare(String(b || ""), undefined, {
    sensitivity: "base",
    numeric: true,
  });

const getCreatedAtMillis = (item) =>
  toMillis(item?.createdAt) ??
  toMillis(item?.created_at) ??
  toMillis(item?.updatedAt) ??
  toMillis(item?.updated_at) ??
  0;

const getSortValue = (item, sortKey, kind) => {
  switch (sortKey) {
    case "createdAt":
      return getCreatedAtMillis(item);

    case "title":
      return item?.title || "";

    case "startdate":
      // Quick Tasks currently use `start`,
      // project tasks use `startdate`.
      return toMillis(item?.startdate ?? item?.start);

    case "enddate":
      // Quick Tasks currently use `end`,
      // project tasks use `enddate`.
      return toMillis(item?.enddate ?? item?.end);

    case "priority":
      return kind === "task"
        ? normalizePriorityValue(item?.priority)
        : null;

    default:
      return null;
  }
};

export const sortItems = (
  items = [],
  options = {},
  kind = "task",
) => {
  const key = options.key || "createdAt";
  const dir = options.dir === "asc" ? 1 : -1;

  return [...items].sort((a, b) => {
    const av = getSortValue(a, key, kind);
    const bv = getSortValue(b, key, kind);

    const aUnsupported = av == null;
    const bUnsupported = bv == null;

    // Unsupported fields remain at the bottom.
    if (aUnsupported && !bUnsupported) return 1;
    if (!aUnsupported && bUnsupported) return -1;

    // If neither supports this field, fall back to created date.
    if (aUnsupported && bUnsupported) {
      const ac = getCreatedAtMillis(a);
      const bc = getCreatedAtMillis(b);

      if (ac !== bc) {
        return (ac - bc) * dir;
      }

      return compareText(a?.title, b?.title);
    }

    if (key === "title") {
      const result = compareText(av, bv);

      if (result !== 0) {
        return result * dir;
      }
    } else if (av !== bv) {
      return (av - bv) * dir;
    }

    // Stable secondary order.
    const ac = getCreatedAtMillis(a);
    const bc = getCreatedAtMillis(b);

    if (ac !== bc) {
      return (ac - bc) * dir;
    }

    return compareText(a?.id, b?.id);
  });
};

export const applyListOptions = (
  items = [],
  options = {},
  kind = "task",
) => {
  let result = items || [];

  if (options.hideDone) {
    result = result.filter(
      (item) => !(item?.done === true || item?.doneStatus === true),
    );
  }

  return sortItems(result, options, kind);
};

// Structural ordering: workspaces/projects always A -> Z.
export const alphabetical = (a, b) =>
  String(a || "").localeCompare(String(b || ""), undefined, {
    sensitivity: "base",
    numeric: true,
  }) || String(a || "").localeCompare(String(b || ""));

export const sortByName = (items = []) =>
  [...items].sort((a, b) => alphabetical(a?.name, b?.name));